import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./QrGalleryDetails.css";
import publicOrigin from "./publicOrigin";
import { clearAuthStorage } from "./auth/logout";

// === IndexedDB Helpers ===
const DB_NAME = "photentra_uploads";
const DB_VERSION = 1;
const STORE_NAME = "pending_files";

function openUploadDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "key" });
        store.createIndex("eventId", "eventId", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeFilesForUpload(galleryId, files) {
  const storeId = `qr_${galleryId}`;
  const prepared = await Promise.all(files.map(async (file) => ({
    key: `${storeId}_${file.name}`,
    eventId: storeId,
    filename: file.name,
    data: await file.arrayBuffer(),
    type: file.type,
    uploaded: false,
  })));
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  for (const item of prepared) store.put(item);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function markFileUploaded(galleryId, filename) {
  const storeId = `qr_${galleryId}`;
  const db = await openUploadDB();
  const key = `${storeId}_${filename}`;
  return new Promise((resolve, reject) => {
    const readTx = db.transaction(STORE_NAME, "readonly");
    const readStore = readTx.objectStore(STORE_NAME);
    const readReq = readStore.get(key);
    readReq.onsuccess = () => {
      const existing = readReq.result;
      if (!existing) { db.close(); resolve(); return; }
      const writeTx = db.transaction(STORE_NAME, "readwrite");
      const writeStore = writeTx.objectStore(STORE_NAME);
      writeStore.put({ ...existing, uploaded: true });
      writeTx.oncomplete = () => { db.close(); resolve(); };
      writeTx.onerror = () => { db.close(); reject(writeTx.error); };
    };
    readReq.onerror = () => { db.close(); reject(readReq.error); };
  });
}

async function getPendingFiles(galleryId) {
  const storeId = `qr_${galleryId}`;
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("eventId");
  return new Promise((resolve, reject) => {
    const req = index.getAll(storeId);
    req.onsuccess = () => { db.close(); resolve(req.result.filter(f => !f.uploaded)); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function getAllStoredFiles(galleryId) {
  const storeId = `qr_${galleryId}`;
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readonly");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("eventId");
  return new Promise((resolve, reject) => {
    const req = index.getAll(storeId);
    req.onsuccess = () => { db.close(); resolve(req.result); };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

async function clearUploadFiles(galleryId) {
  const storeId = `qr_${galleryId}`;
  const db = await openUploadDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);
  const index = store.index("eventId");
  const req = index.getAllKeys(storeId);
  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      for (const key of req.result) store.delete(key);
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    };
    req.onerror = () => { db.close(); reject(req.error); };
  });
}

// Client-side compression
const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.82;
const UPLOAD_CONCURRENCY = 3;
const SLEEP_MS = 300;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function storedFileToDataURL(file) {
  const uint8 = new Uint8Array(file.data);
  let binary = "";
  const chunkSize = 8192;
  for (let j = 0; j < uint8.length; j += chunkSize) {
    binary += String.fromCharCode(...uint8.subarray(j, j + chunkSize));
  }
  return `data:${file.type || "image/jpeg"};base64,${btoa(binary)}`;
}

function compressImageFile(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith("image/")) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) { height = Math.round(height * (MAX_DIMENSION / width)); width = MAX_DIMENSION; }
        else { width = Math.round(width * (MAX_DIMENSION / height)); height = MAX_DIMENSION; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return resolve(file);
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", JPEG_QUALITY);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

function QrGalleryDetails() {
  const { galleryId } = useParams();
  const navigate = useNavigate();

  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [imageCount, setImageCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadJobId, setUploadJobId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const pausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const uploadingFreshRef = useRef(false);
  const pollTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [dragSelecting, setDragSelecting] = useState(false);
  const dragStartRef = useRef(null);

  // QR/Link state
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(""), 4000); return () => clearTimeout(t); } }, [success]);
  useEffect(() => { if (error) { const t = setTimeout(() => setError(""), 5000); return () => clearTimeout(t); } }, [error]);

  const fetchGallery = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-gallery/${galleryId}`);
      const data = await res.json();
      if (res.ok) { setGallery(data.gallery); setImageCount(data.imageCount); }
      else setError(data.message || "Gallery not found");
    } catch { setError("Failed to load gallery"); }
  }, [galleryId]);

  const fetchImages = useCallback(async (p = page) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-gallery/${galleryId}/images?page=${p}&limit=100`);
      const data = await res.json();
      if (res.ok) {
        setImages(data.images);
        setTotalPages(data.totalPages);
        setImageCount(data.total);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [galleryId, page]);

  useEffect(() => {
    fetchGallery();
    fetchImages();
    const savedJobId = localStorage.getItem(`qr_upload_job_${galleryId}`);
    if (savedJobId) {
      setUploadJobId(parseInt(savedJobId));
      setIsUploading(true);
      resumeUpload(parseInt(savedJobId));
    }
  }, [galleryId]);

  useEffect(() => { fetchImages(page); }, [page]);

  // Clean up poll timer on unmount
  useEffect(() => () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); }, []);

  // Poll job status
  const pollJobStatus = useCallback(async (jobId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}`);
      if (!res.ok) { setIsUploading(false); setUploadJobId(null); localStorage.removeItem(`qr_upload_job_${galleryId}`); return; }
      const data = await res.json();
      const { job, completedImages } = data;

      const progress = uploadingFreshRef.current ? 50 + Math.round(job.progress / 2) : job.progress;
      setUploadProgress(progress);
      setUploadStatus(`Stage 2/2 • AI face scan in progress... ${job.completed_images + job.failed_images}/${job.total_images}`);

      if (completedImages && completedImages.length > 0) {
        setImages(prev => {
          const existingIds = new Set(prev.map(img => img.id));
          const newImgs = completedImages.filter(img => !existingIds.has(img.id));
          return newImgs.length > 0 ? [...newImgs, ...prev] : prev;
        });
        setImageCount(prev => prev + completedImages.filter(img => !images.find(i => i.id === img.id)).length);
      }

      if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
        setIsUploading(false); setUploadJobId(null); uploadingFreshRef.current = false; cancelledRef.current = false;
        localStorage.removeItem(`qr_upload_job_${galleryId}`);
        clearUploadFiles(galleryId).catch(() => {});
        if (job.status === 'cancelled') setSuccess(`Upload cancelled. ${job.completed_images} photos processed.`);
        else if (job.status === 'completed') setSuccess(`All ${job.completed_images} photos uploaded!`);
        else setError(`Upload finished with ${job.failed_images} failures.`);
        setUploadProgress(job.status === 'cancelled' ? 0 : 100);
        fetchImages(1); setPage(1);
        setTimeout(() => { setUploadProgress(0); setUploadStatus(""); }, 3000);
        return;
      }
      pollTimerRef.current = setTimeout(() => pollJobStatus(jobId), 2000);
    } catch (err) { pollTimerRef.current = setTimeout(() => pollJobStatus(jobId), 5000); }
  }, [galleryId, images]);

  // Resume upload
  const resumeUpload = useCallback(async (jobId) => {
    try {
      const pendingFiles = await getPendingFiles(galleryId);
      if (pendingFiles.length > 0) {
        setUploadStatus(`Stage 1/2 • Resuming upload of ${pendingFiles.length} remaining photos...`);
        uploadingFreshRef.current = true;
        const allFiles = await getAllStoredFiles(galleryId);
        const totalFiles = allFiles.length;
        let nextIndex = 0, completedCount = 0;
        const alreadyUploaded = totalFiles - pendingFiles.length;
        const workerCount = Math.min(UPLOAD_CONCURRENCY, pendingFiles.length);

        const worker = async () => {
          while (!cancelledRef.current) {
            while (pausedRef.current && !cancelledRef.current) await wait(SLEEP_MS);
            if (cancelledRef.current) return;
            const i = nextIndex++; if (i >= pendingFiles.length) return;
            const file = pendingFiles[i];
            setUploadStatus(`Stage 1/2 • Uploading ${alreadyUploaded + completedCount + 1}/${totalFiles} photos...`);
            try {
              const base64 = storedFileToDataURL(file);
              const r = await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/images`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ imageData: base64, filename: file.filename }),
              });
              if (r.ok) await markFileUploaded(galleryId, file.filename);
            } catch { /* ignore */ }
            finally { completedCount++; setUploadProgress(Math.round(((alreadyUploaded + completedCount) / totalFiles) * 50)); }
          }
        };
        await Promise.all(Array.from({ length: workerCount }, () => worker()));
        if (cancelledRef.current) return;
        await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/finalize`, { method: 'PATCH' });
        setUploadStatus("Stage 2/2 • AI face scan in progress..."); setUploadProgress(50);
      } else {
        uploadingFreshRef.current = false;
        setUploadStatus("Stage 2/2 • AI face scan in progress...");
        await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/finalize`, { method: 'PATCH' }).catch(() => {});
      }
      pollJobStatus(jobId);
    } catch {
      uploadingFreshRef.current = false;
      setUploadStatus("Stage 2/2 • AI face scan in progress...");
      await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/finalize`, { method: 'PATCH' }).catch(() => {});
      pollJobStatus(jobId);
    }
  }, [galleryId, pollJobStatus]);

  // Upload handler
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (imageCount + files.length > 40000) {
      setError(`Cannot upload. Limit is 40,000 photos. Currently ${imageCount}, trying to add ${files.length}.`);
      return;
    }

    setIsUploading(true); setUploadProgress(0); cancelledRef.current = false;
    const validFiles = files.filter(f => f.type.startsWith("image/"));
    if (validFiles.length === 0) { setError("No valid image files selected"); setIsUploading(false); return; }

    try {
      setUploadStatus(`Preparing • Compressing ${validFiles.length} photos...`);
      const compressedFiles = [];
      for (let i = 0; i < validFiles.length; i++) {
        const compressed = await compressImageFile(validFiles[i]);
        compressedFiles.push(compressed);
      }

      setUploadStatus(`Preparing • Saving ${compressedFiles.length} photos for reliable upload...`);
      await storeFilesForUpload(galleryId, compressedFiles);

      const jobRes = await fetch(`${API_BASE_URL}/api/qr-gallery/${galleryId}/upload-job`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ totalImages: compressedFiles.length }),
      });
      if (!jobRes.ok) { const d = await jobRes.json(); throw new Error(d.message || "Failed to create job"); }
      const jobData = await jobRes.json();
      const jobId = jobData.job.id;
      localStorage.setItem(`qr_upload_job_${galleryId}`, jobId.toString());
      setUploadJobId(jobId); uploadingFreshRef.current = true;

      let nextIndex = 0, completedCount = 0, uploadedCount = 0;
      const workerCount = Math.min(UPLOAD_CONCURRENCY, compressedFiles.length);

      const uploadWorker = async () => {
        while (!cancelledRef.current) {
          while (pausedRef.current && !cancelledRef.current) await wait(SLEEP_MS);
          if (cancelledRef.current) return;
          const i = nextIndex++; if (i >= compressedFiles.length) return;
          setUploadStatus(`Stage 1/2 • Uploading ${completedCount + 1}/${compressedFiles.length} photos...`);
          try {
            const base64 = await fileToDataURL(compressedFiles[i]);
            if (cancelledRef.current) return;
            const r = await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/images`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ imageData: base64, filename: compressedFiles[i].name }),
            });
            if (r.ok) { const d = await r.json(); if (!d.skipped) uploadedCount++; await markFileUploaded(galleryId, compressedFiles[i].name); }
          } catch { /* ignore */ }
          finally { completedCount++; setUploadProgress(Math.round((completedCount / compressedFiles.length) * 50)); }
        }
      };
      await Promise.all(Array.from({ length: workerCount }, () => uploadWorker()));
      if (cancelledRef.current) return;

      await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/finalize`, { method: 'PATCH' });
      setUploadStatus(`Stage 2/2 • AI face scan in progress... ${uploadedCount} uploaded`); setUploadProgress(50);
      pollJobStatus(jobId);
    } catch (err) { setError(`Upload failed: ${err.message}`); setIsUploading(false); setUploadProgress(0); setUploadStatus(""); }
  };

  const handlePauseResume = () => {
    const p = !pausedRef.current; pausedRef.current = p; setIsPaused(p);
    if (p) setUploadStatus(prev => `Paused — ${prev}`);
  };

  const handleCancelUpload = async () => {
    cancelledRef.current = true; pausedRef.current = false; setIsPaused(false);
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    const jobId = uploadJobId;
    if (jobId) {
      try { await fetch(`${API_BASE_URL}/api/qr-upload-jobs/${jobId}/cancel`, { method: "POST" }); } catch { /* ignore */ }
    }
    localStorage.removeItem(`qr_upload_job_${galleryId}`);
    setIsUploading(false); setUploadJobId(null); setUploadProgress(0); setUploadStatus("");
    clearUploadFiles(galleryId).catch(() => {});
    fetchImages(1); setPage(1);
  };

  // Selection handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === images.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(images.map(i => i.id)));
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected photos?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/qr-gallery/${galleryId}/delete-images`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageIds: Array.from(selectedIds) }),
      });
      if (!res.ok) throw new Error("Failed to delete");
      setSuccess(`${selectedIds.size} photos deleted`);
      setSelectedIds(new Set()); setSelectMode(false);
      fetchImages(page); fetchGallery();
    } catch (err) { setError(err.message); }
  };

  // Drag select
  const handleMouseDown = (id, e) => {
    if (!selectMode) return;
    e.preventDefault();
    setDragSelecting(true);
    dragStartRef.current = id;
    toggleSelect(id);
  };

  const handleMouseEnter = (id) => {
    if (!dragSelecting) return;
    setSelectedIds(prev => { const n = new Set(prev); n.add(id); return n; });
  };

  const handleMouseUp = () => { setDragSelecting(false); };

  useEffect(() => {
    if (dragSelecting) {
      const up = () => setDragSelecting(false);
      window.addEventListener("mouseup", up);
      return () => window.removeEventListener("mouseup", up);
    }
  }, [dragSelecting]);

  // QR share link
  const shareUrl = gallery ? `${publicOrigin()}/qr/${gallery.token}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleLogout = () => {
    clearAuthStorage();
    navigate("/login");
  };

  if (loading) return <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}><CameraLoader text="Loading gallery..." /></div>;

  return (
    <div className="qrd-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="qrd-main-content">
        <header className="qrd-header">
          <div className="qrd-header-left">
            <button className="qrd-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <button className="qrd-back-btn" onClick={() => navigate("/qr-sharing")}>← Back</button>
            <h1 className="qrd-page-title">{gallery?.name || "QR Gallery"}</h1>
          </div>
          <div className="qrd-header-right">
            <button className="qrd-logout-btn" onClick={handleLogout}>Logout</button>
          </div>
        </header>

        <main className="qrd-main">
          {error && <div className="qrd-error">{error}</div>}
          {success && <div className="qrd-success">{success}</div>}

          {/* Gallery Info + QR */}
          <div className="qrd-info-bar">
            <div className="qrd-info-details">
              {gallery?.event_type && <span className="qrd-info-tag">{gallery.event_type}</span>}
              {gallery?.place && <span className="qrd-info-tag">📍 {gallery.place}</span>}
              {gallery?.event_date && <span className="qrd-info-tag">📅 {new Date(gallery.event_date).toLocaleDateString()}</span>}
              <span className="qrd-info-tag">📸 {imageCount} photos</span>
            </div>
            <div className="qrd-info-actions">
              <button className="qrd-qr-btn" onClick={() => setShowQr(!showQr)}>
                {showQr ? "Hide QR" : "📱 Show QR Code & Link"}
              </button>
            </div>
          </div>

          {/* QR Code Panel */}
          {showQr && (
            <div className="qrd-qr-panel">
              <div className="qrd-qr-code">
                <QRCodeSVG value={shareUrl} size={200} level="H" includeMargin />
              </div>
              <div className="qrd-qr-info">
                <h3>Share this QR Gallery</h3>
                <p>Guests scan this QR code, upload a selfie, and see only their photos.</p>
                <div className="qrd-qr-link-row">
                  <input type="text" readOnly value={shareUrl} className="qrd-qr-link-input" />
                  <button className="qrd-qr-copy-btn" onClick={copyLink}>{copied ? "✓ Copied!" : "📋 Copy"}</button>
                </div>
              </div>
            </div>
          )}

          {/* Upload Section */}
          <div className="qrd-upload-section">
            <div className="qrd-upload-bar">
              <button className="qrd-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                📤 Upload Photos
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} style={{ display: "none" }} />
              <span className="qrd-upload-limit">Up to 40,000 photos</span>

              {!isUploading && images.length > 0 && (
                <div className="qrd-select-controls">
                  <button className={`qrd-select-btn ${selectMode ? "active" : ""}`} onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}>
                    {selectMode ? "Cancel Select" : "☑ Select"}
                  </button>
                  {selectMode && (
                    <>
                      <button className="qrd-select-all-btn" onClick={selectAll}>
                        {selectedIds.size === images.length ? "Deselect All" : "Select All"}
                      </button>
                      {selectedIds.size > 0 && (
                        <button className="qrd-delete-btn" onClick={handleDeleteSelected}>
                          🗑 Delete {selectedIds.size}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {isUploading && (
              <div className="qrd-upload-progress">
                <div className="qrd-progress-bar">
                  <div className="qrd-progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <div className="qrd-progress-info">
                  <span className="qrd-progress-text">{uploadStatus}</span>
                  <span className="qrd-progress-pct">{uploadProgress}%</span>
                </div>
                <div className="qrd-upload-actions">
                  <button className="qrd-pause-btn" onClick={handlePauseResume}>{isPaused ? "▶ Resume" : "⏸ Pause"}</button>
                  <button className="qrd-cancel-btn" onClick={handleCancelUpload}>✕ Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Photo Grid */}
          {images.length === 0 && !isUploading ? (
            <div className="qrd-empty">
              <p>📷</p>
              <p>No photos yet. Upload some to get started!</p>
            </div>
          ) : (
            <>
              <div className="qrd-photo-grid" onMouseUp={handleMouseUp}>
                {images.map((img) => (
                  <div
                    key={img.id}
                    className={`qrd-photo-card ${selectMode ? "selectable" : ""} ${selectedIds.has(img.id) ? "selected" : ""}`}
                    onClick={() => selectMode && toggleSelect(img.id)}
                    onMouseDown={(e) => handleMouseDown(img.id, e)}
                    onMouseEnter={() => handleMouseEnter(img.id)}
                  >
                    <img src={img.thumbnail_url || img.image_url} alt="" loading="lazy" className="qrd-photo-img" />
                    {selectMode && (
                      <div className="qrd-photo-check">
                        {selectedIds.has(img.id) ? "✓" : ""}
                      </div>
                    )}
                    {img.face_detected && <span className="qrd-face-badge">😊</span>}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="qrd-pagination">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="qrd-page-btn">← Prev</button>
                  <span className="qrd-page-info">Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="qrd-page-btn">Next →</button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default QrGalleryDetails;
